import './styles/Filters.scss';

import { Info } from '@mui/icons-material';
import {
  Avatar,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  Grid,
  Input,
  InputBaseComponentProps,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import clsx from 'clsx';
import { endOfDay, getYear, startOfDay } from 'date-fns';
import { debounce } from 'lodash';
import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { CSSProperties } from 'styled-components';

export type Filter = {
  field: string;
  label: string;
  stateHandler?: any;
  inputProps?: InputBaseComponentProps;
  type?: string;
  searchParameter?: boolean;
  withPagination?: boolean;
  hide?: boolean;
  /**
   * @description
   * Used to overwrite the default behaviour to set filters.
   * @param value The newly selected value
   * @param curFilterOptions The currently active Filters
   * @returns The new filterOptions to be set
   */
  filterSetter?: (value: any, curFilterOptions: Record<string, any>) => Record<string, any>;
  toggle?: {
    value: string;
    color?: 'active' | 'inactive' | 'archived' | 'banned' | 'default';
    icon?: React.ReactNode;
    multiple?: boolean;
    onOff?: boolean;
    tooltip?: string;
    stateHandler?: any;
  };
  select?: {
    options: Array<{
      label: string;
      name: string | number | boolean | null;
    }>;
    stateHandler: any;
  };
  multiselect?: {
    options: Array<{
      label: string;
      name: string | number | null;
    }>;
    stateHandler: any;
    customStyle?: CSSProperties;
  };
  switch?: {
    label: string;
    stateHandler: any;
    labelPlacement?: FormControlLabelProps['labelPlacement'];
  };
  checkbox?: {
    label: string;
    stateHandler: any;
    labelPlacement?: FormControlLabelProps['labelPlacement'];
  };
  datePicker?: {
    startTimeAtDay: boolean;
    stateHandler: any;
  };
  urlSwitch?: string;
  jsonPath?: string[];
  large?: boolean;
  quickInfo?: string;
};

interface SelectFilterProps {
  field: Filter['field'];
  value: any;
  exact?: boolean;
  multiple?: boolean;
  urlSwitch?: string;
  jsonPath?: string[];
  filterSetter: Filter['filterSetter'];
}

export type FilterProps = {
  filterConfig: Filter[];
  onFilterChange?: (obj: any) => void; // TODO: change to an appropriate function type
  defaultFilters?: Record<string, any>;
  controls?: React.ReactElement;
};

export const Filters: React.FC<FilterProps> = ({
  filterConfig = [],
  onFilterChange,
  defaultFilters,
  controls
}) => {
  const [filters, setFilters] = useState<Record<string, any>>(defaultFilters || {});
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputChangehandler = useCallback(
    debounce((f: Filter, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onFilterClick({
        field: f.field,
        value: e.target.value,
        jsonPath: f.jsonPath,
        filterSetter: f.filterSetter
      });
      if (f.searchParameter) {
        const value = (e as any).target.value;
        handleSearchParams(f, value);
      }
    }, 500),
    [filterConfig]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedDateChangeHandler = useCallback(
    debounce((f: Filter, e: any) => {
      //Remove the filter from the query
      if (e === null) {
        f.datePicker!.stateHandler[1](null);
        onFilterClick({
          field: f.field,
          value: null,
          jsonPath: f.jsonPath,
          filterSetter: f.filterSetter
        });
        if (f.searchParameter) {
          handleSearchParams(f, null);
        }
      }
      //Add the filter
      else {
        const date = f.datePicker?.startTimeAtDay ? startOfDay(e) : endOfDay(e);
        //Check if the input into the filter is valid
        if (getYear(date) >= 1900) {
          f.datePicker!.stateHandler[1](date);
          onFilterClick({
            field: f.field,
            value: date,
            jsonPath: f.jsonPath,
            filterSetter: f.filterSetter
          });
          if (f.searchParameter) {
            const value = date.toISOString();
            handleSearchParams(f, value);
          }
        }
      }
    }, 500),
    [filterConfig]
  );

  const onFilterClick = ({
    field,
    value,
    exact = false,
    multiple = false,
    urlSwitch = '',
    jsonPath = undefined,
    filterSetter
  }: SelectFilterProps) => {
    if (urlSwitch) {
      return (
        onFilterChange &&
        onFilterChange({
          [urlSwitch]: value === null ? undefined : !!value
        })
      );
    }
    let currentFilters = { ...filters };
    if (filterSetter) {
      currentFilters = { ...filterSetter(value, currentFilters) };
      setFilters(currentFilters);
      onFilterChange && onFilterChange({ filter: JSON.stringify(currentFilters), offset: 0 });
      return;
    }
    if (!currentFilters[field]) {
      currentFilters[field] = {
        exact,
        term: multiple ? [value] : value,
        jsonPath
      };
    } else {
      if (multiple) {
        if (currentFilters[field].term.includes(value)) {
          const index = currentFilters[field].term.indexOf(value);
          currentFilters[field].term.splice(index, 1);
          if (currentFilters[field].term.length === 0) {
            delete currentFilters[field];
          }
        } else {
          currentFilters[field].term.push(value);
        }
      } else {
        if (value === undefined || value === null || (Array.isArray(value) && !value.length)) {
          delete currentFilters[field];
        } else {
          currentFilters[field].term = value;
        }
      }
    }
    setFilters(currentFilters);
    if (value === null) {
      delete currentFilters[field];
    }

    onFilterChange && onFilterChange({ filter: JSON.stringify(currentFilters), offset: 0 });
  };

  const selectOptions = opts => {
    return opts.map(opt => (
      <MenuItem key={opt.name} value={opt.name}>
        {t(opt.label)}
      </MenuItem>
    ));
  };

  const handleSearchParams = (f, value) => {
    const search = location.search;
    const params = new URLSearchParams(search);
    params.set('page', '1');
    if (value) {
      params.set(f.field, value);
      navigate(location.pathname + '?' + params, { replace: true });
    } else {
      params.delete(f.field);
      navigate(location.pathname + '?' + params, { replace: true });
    }
  };

  const ToggleChip = ({ f }) => {
    const isChipDisabled: boolean =
      typeof filters[f.field]?.term === 'number'
        ? !(filters[f.field]?.term === f.toggle.value)
        : !filters[f.field]?.term.includes(f.toggle.value);
    return (
      <Grid item xs={12} md={'auto'}>
        <Tooltip title={f.toggle.tooltip || ''}>
          <Chip
            className={clsx('filters__item', {
              [`filters__item--${f.toggle.color}`]: f.toggle.color,
              [`filters__item--${f.toggle.color}--disabled`]: isChipDisabled
            })}
            label={f.label}
            style={{ marginTop: 6, width: '100%' }}
            clickable
            component={Paper}
            icon={
              f.toggle.icon ? (
                typeof f.toggle.icon === 'string' ? (
                  <Avatar src={f.toggle.icon} style={{ height: '32px' }} />
                ) : (
                  (f.toggle.icon as React.ReactElement)
                )
              ) : undefined
            }
            onClick={() => {
              if (f.toggle.stateHandler) {
                f.toggle.stateHandler[1](filters[f.field]?.term ? false : true);
              }
              onFilterClick({
                exact: true,
                field: f.field,
                value: f.toggle?.onOff && filters[f.field]?.term ? null : f.toggle?.value,
                multiple: f.toggle?.multiple === false ? false : true,
                jsonPath: f.jsonPath,
                filterSetter: f.filterSetter
              });
              if (f.searchParameter) {
                const value = f.toggle?.value;
                handleSearchParams(f, value);
              }
            }}
          />
        </Tooltip>
      </Grid>
    );
  };

  if (filterConfig?.length > 0) {
    return (
      <Grid container className="filters" justifyContent="space-between">
        <Grid container item xs={12} sm alignItems={'center'} gap={1}>
          {filterConfig.map((f, idx) => {
            if (f.hide) {
              return null;
            }
            if (f.toggle) {
              return <ToggleChip f={f} key={`${f.field}_${idx}`} />;
            }

            if (f.select) {
              return (
                <FormControl key={`${f.field}_${idx}`} variant="standard">
                  <InputLabel id={f.label}>{f.label}</InputLabel>
                  <Select
                    labelId={f.label}
                    variant="standard"
                    className="filters__item filters__item--select"
                    value={f.select.stateHandler[0]}
                    label={f.label}
                    onChange={e => {
                      f.select!.stateHandler[1](e.target.value);
                      onFilterClick({
                        field: f.field,
                        value: e.target.value,
                        exact: true,
                        urlSwitch: f.urlSwitch,
                        jsonPath: f.jsonPath,
                        filterSetter: f.filterSetter
                      });
                      if (f.searchParameter !== null && f.searchParameter !== undefined) {
                        const value = (e as any).target.value;
                        handleSearchParams(f, value);
                      }
                    }}
                  >
                    {selectOptions(f.select.options)}
                  </Select>
                </FormControl>
              );
            }

            if (f.multiselect) {
              return (
                <Grid container item xs={'auto'} key={`${f.field}_${idx}`}>
                  <FormControl variant="standard">
                    <InputLabel id={f.label}>{f.label}</InputLabel>
                    <Select
                      labelId={f.label}
                      variant="standard"
                      className="filters__item filters__item--select"
                      value={f.multiselect.stateHandler[0]}
                      label={f.label}
                      multiple
                      hidden
                      onChange={e => {
                        f.multiselect!.stateHandler[1](e.target.value);
                        onFilterClick({
                          field: f.field,
                          value: e.target.value,
                          exact: true,
                          urlSwitch: f.urlSwitch,
                          jsonPath: f.jsonPath,
                          filterSetter: f.filterSetter
                        });
                        if (f.searchParameter) {
                          const value = (e as any).target.value;
                          handleSearchParams(f, value);
                        }
                      }}
                      style={f.multiselect.customStyle}
                    >
                      {selectOptions(f.multiselect.options)}
                    </Select>
                  </FormControl>
                  {f.quickInfo && (
                    <Tooltip
                      title={f.quickInfo || ''}
                      placement="top"
                      style={{ flex: 1, height: '100%' }}
                    >
                      <Info
                        style={{
                          alignSelf: 'center',
                          marginTop: 10
                        }}
                      />
                    </Tooltip>
                  )}
                </Grid>
              );
            }

            if (f.switch) {
              return (
                <FormControl key={`${f.field}_${idx}`} className="filters__item--switch">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={f.switch.stateHandler[0]}
                        onChange={e => {
                          f.switch?.stateHandler[1](e.target.checked);
                          onFilterClick({
                            field: f.field,
                            value: e.target.checked ? true : null,
                            exact: true,
                            filterSetter: f.filterSetter
                          });
                        }}
                        color="primary"
                      />
                    }
                    label={f.label}
                    labelPlacement={f.switch.labelPlacement || 'start'}
                  />
                </FormControl>
              );
            }

            if (f.checkbox) {
              return (
                <FormControl key={`${f.field}_${idx}`} className="filters__item--switch">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={f.checkbox.stateHandler[0]}
                        onChange={e => {
                          f.switch?.stateHandler[1](e.target.checked);
                          onFilterClick({
                            field: f.field,
                            value: e.target.checked ? true : null,
                            exact: true,
                            filterSetter: f.filterSetter
                          });
                        }}
                        color="primary"
                      />
                    }
                    label={f.label}
                    labelPlacement={f.checkbox.labelPlacement || 'start'}
                  />
                </FormControl>
              );
            }

            if (f.datePicker) {
              return (
                <DatePicker
                  key={`${f.field}_${idx}`}
                  renderInput={props => (
                    <TextField
                      {...props}
                      className="filters__item filters__item--select"
                      variant="standard"
                      label={f.label}
                    />
                  )}
                  inputFormat="dd.MM.yyyy"
                  value={f.datePicker.stateHandler[0]}
                  onChange={e => {
                    debouncedDateChangeHandler(f, e);
                  }}
                />
              );
            }
            return (
              <Grid item xs={12} md={'auto'} key={`${f.field}_${idx}`}>
                <FormControl key={`${f.field}_${idx}`} variant="standard" fullWidth>
                  <InputLabel>{f.label}</InputLabel>
                  <Input
                    className={
                      f.large
                        ? 'filters__item filters__item--large'
                        : 'filters__item filters__item--text'
                    }
                    value={f.stateHandler[0]}
                    onChange={e => {
                      f.stateHandler[1](e.target.value);
                      debouncedInputChangehandler(f, e);
                    }}
                    type={f.type}
                    inputProps={f.inputProps}
                  />
                </FormControl>
              </Grid>
            );
          })}
        </Grid>
        <Grid container justifyContent="center" item xs={12} sm="auto" spacing={1}>
          {controls}
        </Grid>
      </Grid>
    );
  } else if (controls) {
    return (
      <Grid container className="filters" justifyContent="end">
        <Grid container justifyContent="center" item xs={12} sm="auto" spacing={1}>
          {controls}
        </Grid>
      </Grid>
    );
  }
  return <></>;
};
